import { VFC, forwardRef, Ref } from 'react';
import Button from '@adsk/alloy-react-button';
import { DatePicker as AlloyDatePicker, DateRangePicker } from '@adsk/alloy-react-date-picker';
import { DatePickerAllArgs } from '@adsk/alloy-react-date-picker/es/types';
import styled from 'styled-components';
import { translate } from '../../helpers';
import { getFooterButtonAttributes, DateRange } from './helpers/rangeHelpers';
import { FooterButton } from './types';

export type DatePickerProps = Omit<
  DatePickerAllArgs,
  'onChange' | 'value' | 'initialOpen' | 'initialStartDate' | 'initialEndDate'
> & {
  footerButtons?: FooterButton[];
  'data-testId'?: string;
  ref?: Ref<HTMLDivElement>;
  range?: boolean;
  onChange: (change?: DateRange | Date | null) => void;
  date?: Date | null;
  defaultStartDate?: Date | null;
  defaultEndDate?: Date | null;
  defaultOpen?: boolean;
};

const ActionsFooterWrapper = styled.div`
  & > button {
    margin: 0 8px 8px 0;
  }
`;

const PureDatePicker: VFC<DatePickerProps> = forwardRef(
  (
    {
      'data-testId': dataTestId = 'DatePicker',
      footerButtons,
      onChange,
      range,
      timeZone,
      date,
      defaultStartDate: initialStartDate,
      defaultEndDate: initialEndDate,
      defaultOpen: initialOpen,
      ...datePickerProps
    },
    ref,
  ) => {
    const generateFooterButton = (button: FooterButton) => {
      const { label, getRange } = getFooterButtonAttributes(button);

      return (
        <Button
          key={button}
          size={Button.SIZES.SMALL}
          onClick={() => {
            const buttonDateRange = getRange(timeZone);
            onChange(range ? buttonDateRange : buttonDateRange[0]);
          }}
        >
          {label}
        </Button>
      );
    };

    const commonProps = {
      'data-testid': dataTestId,
      ref,
      placeholder: translate('date.placeholder'),
      style: { '> button': { width: '100%' } },
      timeZone,
      initialOpen,
      renderFooterActions: () =>
        footerButtons ? (
          <ActionsFooterWrapper>{footerButtons?.map(generateFooterButton)}</ActionsFooterWrapper>
        ) : null,
    };

    return range ? (
      <DateRangePicker
        initialStartDate={initialStartDate}
        initialEndDate={initialEndDate}
        allowSameStartAndEndDate
        onChange={({ startDate, endDate }) => onChange([startDate, endDate])}
        {...commonProps}
        {...datePickerProps}
      />
    ) : (
      <AlloyDatePicker
        onChange={({ date }) => onChange(date)}
        value={date}
        {...commonProps}
        {...datePickerProps}
      />
    );
  },
);

export const DatePicker = Object.assign(PureDatePicker, {
  VARIANTS: AlloyDatePicker.VARIANTS,
  VALID_FORMATS: AlloyDatePicker.VALID_FORMATS,
  Footer: AlloyDatePicker.Footer,
  Trigger: AlloyDatePicker.Trigger,
});
